import { useRef, useState } from 'react';

interface SignaturePadProps {
  /** Appelé avec le PNG signé (blob prêt à envoyer), ou `null` si la signature est effacée. */
  onChange: (blob: Blob | null) => void;
}

/** Pavé de signature tactile minimal : dessin au doigt/souris sur un canevas, export en PNG.
 * Pas de dépendance externe — juste assez pour capturer une signature chauffeur (preuve de remise). */
export function SignaturePad({ onChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const empty = useRef(true);
  const [hasSignature, setHasSignature] = useState(false);

  function ctx() {
    return canvasRef.current?.getContext('2d') || null;
  }

  function point(e: React.PointerEvent<HTMLCanvasElement>) {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    drawing.current = true;
    const c = ctx();
    if (!c) return;
    const { x, y } = point(e);
    c.beginPath();
    c.moveTo(x, y);
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    e.preventDefault();
    const c = ctx();
    if (!c) return;
    const { x, y } = point(e);
    c.lineWidth = 2;
    c.lineCap = 'round';
    c.strokeStyle = '#15181D';
    c.lineTo(x, y);
    c.stroke();
    empty.current = false;
  }

  function end() {
    drawing.current = false;
    if (!empty.current) {
      setHasSignature(true);
      canvasRef.current?.toBlob((b) => onChange(b), 'image/png');
    }
  }

  function clear() {
    const c = ctx();
    const canvas = canvasRef.current;
    if (c && canvas) c.clearRect(0, 0, canvas.width, canvas.height);
    empty.current = true;
    setHasSignature(false);
    onChange(null);
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={320}
        height={120}
        className="signature-pad"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <div className="row" style={{ marginTop: '0.4rem' }}>
        <button type="button" className="small" onClick={clear}>Effacer</button>
        {hasSignature && <span className="badge ok">signée</span>}
      </div>
    </div>
  );
}
